import React, { useState, useEffect, useCallback } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { getDRE, getDRETransactions, DREEntry, DRETransaction } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import { getMonthRange } from '../hooks/dashboardUtils';
import { X } from 'lucide-react';

interface DrilldownParams {
  year: number;
  month: number;
  label: string;
  category: string;
  categoryLabel: string;
}

export default function DREPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<DREEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drilldown, setDrilldown] = useState<DrilldownParams | null>(null);
  const [drilldownData, setDrilldownData] = useState<DRETransaction[] | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const { end } = getMonthRange(year, month);
    const beginYear = year - 1;
    const begin = `${beginYear}-${String(month).padStart(2, '0')}-01`;
    getDRE({ begin, end })
      .then((result) => { if (!cancelled) setData(result.months); })
      .catch((err: any) => { if (!cancelled) setError(err.response?.data?.detail || 'Erro ao carregar DRE'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, month]);

  const openDrilldown = useCallback(async (params: DrilldownParams) => {
    setDrilldown(params);
    setDrilldownData(null);
    setDrilldownLoading(true);
    try {
      const result = await getDRETransactions({
        year: params.year,
        month: params.month,
        category: params.category,
      });
      setDrilldownData(result.transactions);
    } catch {
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  if (loading && !data) return <LoadingSpinner message="Carregando DRE..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data || data.length === 0) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  const hasFin = data.some(r => r.receita_financeira > 0 || r.despesas_financeiras > 0);

  const clickable = "cursor-pointer hover:underline decoration-dotted";
  const valColor = (v: number) => v >= 0 ? 'text-green-700' : 'text-red-700';

  // DRE rows definition: each row is a line in the vertical DRE
  type RowDef = {
    key: string;
    label: string;
    getValue: (r: DREEntry) => number;
    style: 'normal' | 'subtotal' | 'total';
    indent?: boolean;
    sign?: '+' | '-';
    clickCategory?: string;
    clickLabel?: string;
    showIf?: boolean;
  };

  const rows: RowDef[] = [
    { key: 'receita_servicos', label: 'Receita Bruta de Serviços', getValue: r => r.receita_servicos, style: 'normal', clickCategory: 'receita_servicos', clickLabel: 'Receita de Serviços' },
    { key: 'despesas_op', label: '(-) Despesas Operacionais', getValue: r => r.despesas_operacionais, style: 'normal', sign: '-', clickCategory: 'despesas_operacionais', clickLabel: 'Despesas Operacionais' },
    { key: 'resultado_op', label: 'Resultado Operacional', getValue: r => r.resultado_operacional, style: 'subtotal' },
    { key: 'receita_fin', label: '(+) Receitas Financeiras', getValue: r => r.receita_financeira, style: 'normal', indent: true, sign: '+', clickCategory: 'receita_financeira', clickLabel: 'Receitas Financeiras', showIf: hasFin },
    { key: 'despesas_fin', label: '(-) Despesas Financeiras', getValue: r => r.despesas_financeiras, style: 'normal', indent: true, sign: '-', clickCategory: 'despesas_financeiras', clickLabel: 'Despesas Financeiras', showIf: hasFin },
    { key: 'resultado_fin', label: 'Resultado Financeiro', getValue: r => r.resultado_financeiro, style: 'subtotal', showIf: hasFin },
    { key: 'resultado', label: 'Resultado Líquido', getValue: r => r.resultado, style: 'total' },
    { key: 'resultado_acum', label: 'Resultado Acumulado', getValue: r => r.resultado_acumulado, style: 'total' },
  ];

  const visibleRows = rows.filter(r => r.showIf === undefined || r.showIf);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
      </div>

      {/* Chart */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Demonstrativo de Resultado (DRE)
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="bars" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="line" orientation="right" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => formatBRL(Number(value))} />
              <Legend />
              <Bar yAxisId="bars" dataKey="receitas" name="Receitas" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="bars" dataKey="despesas" name="Despesas" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line yAxisId="line" type="monotone" dataKey="resultado_acumulado" name="Resultado Acumulado" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* DRE Table — vertical/deductive format */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Detalhamento Mensal
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-3 font-medium text-gray-500 sticky left-0 z-10 bg-white min-w-[220px]">Conta</th>
                  {data.map(row => (
                    <th key={`${row.year}-${row.month}`} className="text-right py-3 px-3 font-medium text-gray-500 min-w-[110px]">
                      {row.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((rowDef) => {
                  const isSubtotal = rowDef.style === 'subtotal';
                  const isTotal = rowDef.style === 'total';
                  const isBold = isSubtotal || isTotal;
                  const borderClass = isSubtotal ? 'border-t border-gray-300' : isTotal ? 'border-t-2 border-gray-400' : 'border-b border-gray-100';
                  const bgClass = isTotal ? 'bg-gray-50' : isSubtotal ? 'bg-gray-50/50' : '';
                  const stickyBg = isTotal ? 'bg-gray-50' : isSubtotal ? 'bg-gray-50' : 'bg-white';

                  return (
                    <tr key={rowDef.key} className={`${borderClass} ${bgClass}`}>
                      <td className={`py-2.5 px-3 text-gray-700 sticky left-0 z-10 ${stickyBg} ${isBold ? 'font-semibold' : ''} ${rowDef.indent ? 'pl-6' : ''}`}>
                        {rowDef.label}
                      </td>
                      {data.map((entry) => {
                        const val = rowDef.getValue(entry);
                        const hasClick = !!rowDef.clickCategory;
                        return (
                          <td
                            key={`${entry.year}-${entry.month}`}
                            className={`py-2.5 px-3 text-right font-mono ${isBold ? 'font-semibold' : ''} ${valColor(rowDef.sign === '-' ? -val : val)}`}
                          >
                            {hasClick ? (
                              <span
                                className={clickable}
                                onClick={() => openDrilldown({
                                  year: entry.year, month: entry.month, label: entry.label,
                                  category: rowDef.clickCategory!, categoryLabel: rowDef.clickLabel!,
                                })}
                              >
                                {formatBRL(val)}
                              </span>
                            ) : (
                              formatBRL(val)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDrilldown(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {drilldown.categoryLabel}
                </h3>
                <p className="text-sm text-gray-500">{drilldown.label}</p>
              </div>
              <button onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {drilldownLoading ? (
                <div className="text-center py-8 text-gray-400">Carregando...</div>
              ) : !drilldownData || drilldownData.length === 0 ? (
                <div className="text-center py-8 text-gray-400">Nenhuma transação</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Data</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Descrição</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Conta</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Valor</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-600">Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownData.map((txn, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-50">
                        <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{txn.date}</td>
                        <td className="py-2 px-3 text-gray-800">{txn.payee}</td>
                        <td className="py-2 px-3 text-gray-500 text-xs">{txn.account}</td>
                        <td className="py-2 px-3 text-right font-mono text-gray-800">{formatBRL(txn.amount)}</td>
                        <td className="py-2 px-3 text-right font-mono text-blue-600 font-medium">{formatBRL(txn.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
